/**
 * Device detection utilities for mobile optimization
 */

/**
 * Check if the current device is mobile
 */
export const isMobile = (): boolean => {
  if (typeof window === 'undefined') return false;
  
  // Check screen size first (most reliable)
  const isSmallScreen = window.innerWidth <= 768;
  
  // Check user agent as secondary check
  const userAgent = navigator.userAgent.toLowerCase();
  const mobileKeywords = ['mobile', 'android', 'iphone', 'ipod', 'blackberry', 'windows phone'];
  const isMobileUserAgent = mobileKeywords.some(keyword => userAgent.includes(keyword));
  
  // Check for touch support
  const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  
  return isSmallScreen || (isMobileUserAgent && isTouchDevice);
};

/**
 * Check if the current device is tablet
 */
export const isTablet = (): boolean => {
  if (typeof window === 'undefined') return false;
  
  const isTabletScreen = window.innerWidth > 768 && window.innerWidth <= 1024;
  const userAgent = navigator.userAgent.toLowerCase();
  const isTabletUserAgent = userAgent.includes('tablet') || userAgent.includes('ipad');
  
  return isTabletScreen || isTabletUserAgent;
};

/**
 * Check if the current device is desktop
 */
export const isDesktop = (): boolean => {
  return !isMobile() && !isTablet();
};

/**
 * Get the device type
 */
export const getDeviceType = (): 'mobile' | 'tablet' | 'desktop' => {
  if (isMobile()) return 'mobile';
  if (isTablet()) return 'tablet';
  return 'desktop';
};

/**
 * Check if the device supports touch
 */
export const isTouchDevice = (): boolean => {
  if (typeof window === 'undefined') return false;
  return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
};

/**
 * Check if the device is iOS
 */
export const isIOS = (): boolean => {
  if (typeof window === 'undefined') return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent);
};

/**
 * Check if the device is Android
 */
export const isAndroid = (): boolean => {
  if (typeof window === 'undefined') return false;
  return /Android/.test(navigator.userAgent);
};

/**
 * Get device pixel ratio for high-DPI displays
 */
export const getDevicePixelRatio = (): number => {
  if (typeof window === 'undefined') return 1;
  return window.devicePixelRatio || 1;
};

/**
 * Check if the device prefers reduced motion
 */
export const prefersReducedMotion = (): boolean => {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
};

/**
 * Get viewport dimensions
 */
export const getViewportDimensions = () => {
  if (typeof window === 'undefined') return { width: 0, height: 0 };
  
  return {
    width: window.innerWidth,
    height: window.innerHeight
  };
};

/**
 * Check if the device is in landscape mode
 */
export const isLandscape = (): boolean => {
  if (typeof window === 'undefined') return false;
  return window.innerWidth > window.innerHeight;
};

/**
 * Check if the device is in portrait mode
 */
export const isPortrait = (): boolean => {
  return !isLandscape();
};
